<template>
  <main class="recharge-page">
    <section class="recharge-card" aria-labelledby="recharge-title">
      <header class="recharge-header">
        <img class="recharge-logo" :src="logoMark" :alt="appName" />
        <div>
          <p class="recharge-kicker">{{ appName }} Wallet</p>
          <h1 id="recharge-title">Recharge Coins</h1>
          <p class="recharge-lede">Choose a secure payment channel available for your country or region.</p>
        </div>
      </header>

      <form class="user-form" @submit.prevent="confirmUser">
        <label for="sawa-user-id">{{ appName }} User ID</label>
        <div class="user-form-row">
          <input id="sawa-user-id" v-model.trim="userId" type="text" autocomplete="off" placeholder="Enter your user ID" :disabled="Boolean(h5Token)" />
          <button type="submit" :disabled="userLoading || !userId">{{ userLoading ? 'Checking...' : 'Confirm' }}</button>
        </div>
        <p v-if="user" class="user-confirmed">Charging account: {{ userName }} ({{ userId }})</p>
      </form>

      <p v-if="error" class="status-message error-message" role="alert">{{ error }}</p>
      <p v-if="notice" class="status-message notice-message">{{ notice }}</p>
      <p v-if="loading" class="status-message">Loading available payment channels...</p>

      <template v-if="!loading">
        <section class="recharge-section" aria-labelledby="region-title">
          <div class="section-heading">
            <h2 id="region-title">Country &amp; Region</h2>
            <span>{{ selectedCountryName }}</span>
          </div>
          <select v-model="country" aria-label="Country and region" @change="changeCountry">
            <option v-for="item in countries" :key="item.code" :value="item.code">{{ countryLabel(item) }}</option>
          </select>
        </section>

        <section class="recharge-section" aria-labelledby="channel-title">
          <div class="section-heading">
            <h2 id="channel-title">Payment Channel</h2>
            <span>{{ channels.length }} available</span>
          </div>
          <div class="option-grid">
            <button v-for="item in channels" :key="item.code" type="button" :class="['option-button', { selected: item.code === channelCode }]" @click="selectChannel(item)">
              {{ item.label }}
            </button>
          </div>
        </section>

        <section v-if="selectedChannel && selectedChannel.methods.length" class="recharge-section" aria-labelledby="method-title">
          <div class="section-heading">
            <h2 id="method-title">{{ isCrypto ? 'Payment Token' : 'Payment Method' }}</h2>
            <span v-if="isCrypto">Network matters - send on the chain you pick</span>
          </div>
          <div class="option-grid">
            <button v-for="method in selectedChannel.methods" :key="method" type="button" :class="['option-button', { selected: method === methodCode }]" @click="methodCode = method">
              {{ methodLabel(method) }}
            </button>
          </div>
        </section>

        <section v-if="requiresEmail && !requiresContactInfo" class="contact-section" aria-labelledby="email-title">
          <div class="section-heading">
            <h2 id="email-title">Payment Email</h2>
            <span>Required by this channel</span>
          </div>
          <input v-model.trim="email" type="email" autocomplete="email" placeholder="Email address" />
          <p class="fine-print">Cregis sends the payment receipt here. Nothing else is emailed to you.</p>
        </section>

        <section v-if="requiresContactInfo" class="contact-section" aria-labelledby="contact-title">
          <div class="section-heading">
            <h2 id="contact-title">Payment Contact</h2>
            <span>Required by this channel</span>
          </div>
          <div class="contact-grid">
            <input v-model.trim="phone" type="tel" autocomplete="tel" placeholder="Phone number" />
            <input v-model.trim="email" type="email" autocomplete="email" placeholder="Email address" />
          </div>
        </section>

        <section v-if="cryptoOrder" class="recharge-section payment-panel" aria-labelledby="pay-title">
          <div class="section-heading">
            <h2 id="pay-title">Send {{ cryptoOrder.currency }}</h2>
            <span>{{ paymentStatusLabel }}</span>
          </div>

          <template v-if="cryptoOrder.status === 0">
            <p class="pay-lede">
              Send <b>exactly {{ cryptoOrder.amount }} {{ cryptoOrder.currency }}</b> on the
              <b>{{ cryptoOrder.chainName }}</b> network to the address below.
              {{ formatCoins(cryptoOrder.coins) }} coins are credited once the network confirms it.
            </p>

            <img v-if="qrDataUrl" class="pay-qr" :src="qrDataUrl" :alt="'QR code for ' + cryptoOrder.depositAddress" />

            <label class="pay-label" for="deposit-address">Deposit address</label>
            <div class="pay-address-row">
              <input id="deposit-address" :value="cryptoOrder.depositAddress" readonly @focus="$event.target.select()" />
              <button type="button" @click="copyAddress">{{ copied ? 'Copied' : 'Copy' }}</button>
            </div>

            <dl class="pay-facts">
              <div><dt>Amount</dt><dd>{{ cryptoOrder.amount }} {{ cryptoOrder.currency }}</dd></div>
              <div><dt>Network</dt><dd>{{ cryptoOrder.chainName }} ({{ cryptoOrder.payToken }})</dd></div>
              <div><dt>Expires in</dt><dd>{{ countdown }}</dd></div>
            </dl>

            <p class="fine-print">
              Send only {{ cryptoOrder.currency }} on {{ cryptoOrder.chainName }}. Another token or
              another network cannot be credited automatically. Sending less than the exact amount
              holds the order for manual review. This page updates on its own once the deposit lands.
            </p>
          </template>

          <p v-else-if="cryptoOrder.status === 1" class="status-message notice-message">
            Payment confirmed. {{ formatCoins(cryptoOrder.coins) }} coins have been added to your wallet.
          </p>
          <p v-else-if="cryptoOrder.status === 2" class="status-message error-message">
            This order expired before a deposit arrived. Pick a coin pack to start a new one.
          </p>
          <p v-else class="status-message error-message">
            This payment needs a manual check. Contact support with order {{ cryptoOrder.orderId }}.
          </p>

          <button type="button" class="pay-restart" @click="clearCryptoOrder">Choose another pack</button>
        </section>

        <section v-if="!cryptoOrder" class="recharge-section" aria-labelledby="amount-title">
          <div class="section-heading">
            <h2 id="amount-title">Recharge Amount</h2>
            <span>{{ displayCurrency }}</span>
          </div>
          <p v-if="bonusPercent" class="bonus-banner">
            +{{ bonusPercent }}% bonus coins on every pack bought here, on top of the pack's own bonus.
          </p>
          <div class="product-grid">
            <button v-for="product in products" :key="product.id" type="button" class="product-card" :disabled="submitting" @click="startOrder(product)">
              <strong>{{ formatCoins(product.coinAmount) }} coins</strong>
              <span v-if="product.bonusCoins">+{{ formatCoins(product.bonusCoins) }} bonus</span>
              <b>{{ product.currency }} {{ product.price }}</b>
            </button>
          </div>
          <p v-if="isCrypto" class="fine-print">
            Coin packs are priced in USD. The exact crypto amount and the receiving address are
            locked on the Cregis checkout page and stay payable for {{ cryptoValidMinutes }} minutes.
            Coins land in your wallet once the network confirms the transfer.
          </p>
          <p v-else class="fine-print">Prices, taxes, and available payment methods are confirmed at checkout.</p>
        </section>
      </template>
    </section>
  </main>
</template>

<script>
import QRCode from 'qrcode'
import logoMark from '../assets/sawa-party-logo.webp'
import { APP_NAME } from '../config/app'
import {
  CREGIS_VALID_MINUTES,
  CRYPTO_CURRENCY,
  cryptoTokenLabel,
  GLOBAL_REGION,
  isCryptoChannel,
  isGlobalRegion
} from '../config/crypto-recharge'
import { isRestrictedCountry } from '../config/restricted-countries'
import {
  checkoutUrl,
  createProviderOrder,
  exchangeHandoff,
  getCapabilities,
  getProducts,
  getCryptoOrder,
  getUserByDanaId,
  RechargeApiError
} from '../api/recharge'

export default {
  name: 'RechargePage',
  data() {
    return {
      appName: APP_NAME,
      logoMark,
      h5Token: '',
      userId: '',
      user: null,
      userLoading: false,
      loading: true,
      error: '',
      notice: '',
      cryptoValidMinutes: CREGIS_VALID_MINUTES,
      countries: [],
      country: '',
      currency: 'USD',
      channels: [],
      channelCode: '',
      methodCode: '',
      products: [],
      phone: '',
      email: '',
      submitting: false,
      cryptoOrder: null,
      qrDataUrl: '',
      copied: false,
      now: Date.now(),
      pollTimer: null,
      clockTimer: null
    }
  },
  computed: {
    selectedChannel() {
      return this.channels.find(item => item.code === this.channelCode) || null
    },
    selectedCountryName() {
      const selected = this.countries.find(item => item.code === this.country)
      return selected ? selected.name : this.country || 'Select a country'
    },
    userName() {
      return this.user && (this.user.nickname || this.user.name || this.user.nickName || this.user.uid) || 'Verified user'
    },
    requiresContactInfo() {
      return Boolean(this.selectedChannel && this.selectedChannel.requiresContactInfo)
    },
    requiresEmail() {
      return Boolean(this.selectedChannel && this.selectedChannel.requiresEmail)
    },
    isCrypto() {
      return isCryptoChannel(this.channelCode)
    },
    /** Admin third-party bonus percent; each product carries it, and it is already inside bonusCoins. */
    bonusPercent() {
      return this.products.reduce((max, product) => Math.max(max, Number(product.bonusPercent) || 0), 0)
    },
    displayCurrency() {
      return this.isCrypto ? CRYPTO_CURRENCY : this.currency
    },
    paymentStatusLabel() {
      const labels = { 0: 'Waiting for your transfer', 1: 'Confirmed', 2: 'Expired', 3: 'Needs review' }
      return labels[this.cryptoOrder && this.cryptoOrder.status] || ''
    },
    countdown() {
      if (!this.cryptoOrder || !this.cryptoOrder.expireTime) return '--:--'
      const left = Math.max(0, this.cryptoOrder.expireTime - this.now)
      const minutes = Math.floor(left / 60000)
      const seconds = Math.floor((left % 60000) / 1000)
      return `${minutes}:${String(seconds).padStart(2, '0')}`
    }
  },
  async mounted() {
    this.readCheckoutReturn()
    await this.exchangeAppHandoff()
    await this.loadCapabilities()
    const queryUserId = new URLSearchParams(window.location.search).get('danaId')
    if (!this.userId && queryUserId) this.userId = queryUserId
  },
  beforeUnmount() {
    this.stopWatchingOrder()
  },
  methods: {
    async exchangeAppHandoff() {
      const ticket = new URLSearchParams(window.location.search).get('handoff')
      if (!ticket) return
      try {
        const session = await exchangeHandoff(ticket)
        this.h5Token = session && session.token || ''
        this.userId = session && session.userId ? String(session.userId) : ''
        if (this.h5Token && this.userId) await this.confirmUser()
      } catch (error) {
        this.error = this.readableError(error, 'The recharge session could not be opened.')
      }
    },
    async loadCapabilities() {
      this.loading = true
      try {
        const data = await getCapabilities({ country: this.country, currency: this.currency })
        const countries = Array.isArray(data && data.countries) ? data.countries : []
        this.countries = [GLOBAL_REGION, ...countries.filter(item => !isRestrictedCountry(item.code))]
        const preferred = data && data.country || this.country
        if (preferred && !isRestrictedCountry(preferred)) {
          this.country = preferred
          this.currency = data && data.currency || this.currency
          this.setChannelState(data && data.channels)
        } else {
          // The API's default lands on a restricted country: move to the first allowed one and
          // load its channels, never the restricted country's.
          const fallback = this.countries.find(item => !isGlobalRegion(item.code)) || GLOBAL_REGION
          this.country = fallback.code
          await this.changeCountry()
        }
      } catch (error) {
        this.error = this.readableError(error, 'Payment channels could not be loaded.')
      } finally {
        this.loading = false
      }
    },
    async changeCountry() {
      this.error = ''
      if (isRestrictedCountry(this.country)) {
        this.error = 'Recharge is not available in this country or region.'
        this.setChannelState([])
        return
      }
      try {
        if (isGlobalRegion(this.country)) {
          const data = await getCapabilities({ currency: CRYPTO_CURRENCY })
          const channels = Array.isArray(data && data.channels) ? data.channels : []
          this.currency = CRYPTO_CURRENCY
          this.setChannelState(channels.filter(item => isCryptoChannel(item.code)))
          return
        }
        const data = await getCapabilities({ country: this.country, currency: this.currency })
        this.currency = data && data.currency || this.currency
        this.setChannelState(data && data.channels)
      } catch (error) {
        this.error = this.readableError(error, 'Payment channels could not be loaded for this region.')
        this.channels = []
        this.products = []
      }
    },
    setChannelState(channels) {
      this.channels = Array.isArray(channels) ? channels : []
      const selected = this.channels.find(item => item.code === this.channelCode) || this.channels[0]
      this.channelCode = selected ? selected.code : ''
      this.methodCode = selected && selected.methods && selected.methods[0] || ''
      this.loadProducts()
    },
    async loadProducts() {
      if (!this.channelCode) {
        this.products = []
        return
      }
      try {
        const data = await getProducts({ channel: this.channelCode, country: this.country, currency: this.currency })
        this.products = Array.isArray(data) ? data : []
      } catch (error) {
        this.products = []
        this.error = this.readableError(error, 'Coin packs could not be loaded.')
      }
    },
    selectChannel(channel) {
      this.channelCode = channel.code
      this.methodCode = channel.methods && channel.methods[0] || ''
      this.loadProducts()
    },
    async confirmUser() {
      if (!this.userId || this.userLoading) return
      this.userLoading = true
      this.error = ''
      try {
        this.user = await getUserByDanaId(this.userId, this.h5Token)
        if (!this.user) throw new RechargeApiError('The user ID could not be verified.', 'USER_NOT_FOUND')
      } catch (error) {
        this.user = null
        this.error = this.readableError(error, 'The user ID could not be verified.')
      } finally {
        this.userLoading = false
      }
    },
    async startOrder(product) {
      if (this.submitting) return
      if (!this.user) {
        this.error = 'Confirm your user ID before choosing a coin pack.'
        return
      }
      if (!this.country || isRestrictedCountry(this.country)) {
        this.error = 'Recharge is not available in this country or region.'
        return
      }
      if (this.requiresContactInfo && (!this.phone || !this.email)) {
        this.error = 'Enter your phone number and email before choosing a coin pack.'
        return
      }
      if (this.requiresEmail && !this.email) {
        this.error = 'Enter your email address before choosing a coin pack.'
        return
      }
      this.submitting = true
      this.error = ''
      this.notice = ''
      try {
        const result = await createProviderOrder({
          channel: this.channelCode,
          method: this.methodCode,
          product,
          userId: this.user.uid || this.user.id || this.userId,
          country: this.country,
          currency: this.currency,
          phone: this.phone,
          email: this.email,
          token: this.h5Token
        })
        // A WaaS project has no hosted checkout: the player sends the deposit themselves, so the
        // page shows the address and watches the order instead of redirecting. The response is
        // trusted over the selected channel, so a page left open across a deploy still works.
        if (this.isCrypto || (result && result.depositAddress)) {
          await this.showCryptoOrder(result)
          return
        }
        const url = checkoutUrl(result)
        if (!url) throw new RechargeApiError('The payment link was not returned.', 'MISSING_CHECKOUT_URL')
        window.location.assign(url)
      } catch (error) {
        this.error = this.readableError(error, 'The order could not be created. Please try another channel.')
      } finally {
        this.submitting = false
      }
    },
    async showCryptoOrder(order) {
      if (!order || !order.depositAddress) {
        throw new RechargeApiError('No deposit address was returned.', 'MISSING_DEPOSIT_ADDRESS')
      }
      this.cryptoOrder = order
      this.copied = false
      this.qrDataUrl = await this.renderQr(order.depositAddress)
      this.startWatchingOrder()
    },
    async renderQr(address) {
      try {
        return await QRCode.toDataURL(address, { margin: 1, width: 240 })
      } catch (error) {
        // The address and the copy button are enough on their own.
        return ''
      }
    },
    startWatchingOrder() {
      this.stopWatchingOrder()
      this.now = Date.now()
      this.clockTimer = setInterval(() => { this.now = Date.now() }, 1000)
      this.pollTimer = setInterval(this.refreshCryptoOrder, 8000)
    },
    stopWatchingOrder() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      if (this.clockTimer) clearInterval(this.clockTimer)
      this.pollTimer = null
      this.clockTimer = null
    },
    async refreshCryptoOrder() {
      if (!this.cryptoOrder) return
      try {
        const fresh = await getCryptoOrder(this.cryptoOrder.orderId, this.h5Token)
        if (!fresh) return
        this.cryptoOrder = fresh
        if (fresh.status !== 0) this.stopWatchingOrder()
      } catch (error) {
        // A failed poll is not worth interrupting the payment over; the next tick tries again.
      }
    },
    async copyAddress() {
      try {
        await navigator.clipboard.writeText(this.cryptoOrder.depositAddress)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (error) {
        this.error = 'Copying failed. Select the address and copy it manually.'
      }
    },
    clearCryptoOrder() {
      this.stopWatchingOrder()
      this.cryptoOrder = null
      this.qrDataUrl = ''
    },
    countryLabel(item) {
      return isGlobalRegion(item.code) ? item.name : `${item.name} (${item.code})`
    },
    methodLabel(method) {
      if (this.isCrypto) return cryptoTokenLabel(method)
      const labels = { CARD: 'CardPay', STC: 'STC Pay', SBP: 'SBP' }
      return labels[method] || method
    },
    readCheckoutReturn() {
      const result = new URLSearchParams(window.location.search).get('recharge')
      if (result === 'success') {
        this.notice = 'Payment received. Coins are credited once the payment is confirmed.'
      } else if (result === 'cancel') {
        this.notice = 'That payment was cancelled. Pick a coin pack to try again.'
      }
    },
    formatCoins(value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    readableError(error, fallback) {
      return error && error.message ? error.message : fallback
    }
  }
}
</script>

<style scoped>
.recharge-page {
  min-height: calc(100vh - 84px);
  padding: 56px 20px 86px;
  background: radial-gradient(circle at top right, #f3c8ff 0, transparent 42%), linear-gradient(145deg, #fff4fb, #efe2ff);
}

.recharge-card {
  width: min(760px, 100%);
  margin: 0 auto;
  padding: 32px;
  border: 1px solid rgba(131, 57, 154, 0.15);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 24px 70px rgba(81, 29, 111, 0.16);
}

.recharge-header {
  display: flex;
  gap: 20px;
  align-items: center;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(131, 57, 154, 0.12);
}

.recharge-logo {
  width: 52px;
  height: 52px;
  border-radius: 15px;
  object-fit: cover;
  flex: 0 0 auto;
  box-shadow: 0 8px 20px rgba(60, 30, 10, 0.2);
}

.recharge-kicker,
.section-heading span,
.fine-print,
.user-confirmed {
  color: var(--color-muted);
  font-size: 0.86rem;
  font-weight: 800;
}

.recharge-kicker {
  margin: 0 0 6px;
  color: var(--color-hot);
  text-transform: uppercase;
}

h1,
h2,
p {
  margin-top: 0;
}

h1 {
  margin-bottom: 8px;
  font-size: clamp(2rem, 5vw, 3rem);
}

.recharge-lede {
  margin-bottom: 0;
  color: var(--color-muted);
  line-height: 1.6;
}

.user-form {
  padding: 26px 0 4px;
}

.user-form label {
  display: block;
  margin-bottom: 8px;
  font-weight: 900;
}

.user-form-row {
  display: flex;
  gap: 10px;
}

input,
select {
  min-height: 46px;
  width: 100%;
  padding: 0 14px;
  border: 1px solid rgba(131, 57, 154, 0.18);
  border-radius: 12px;
  color: var(--color-ink);
  background: #fff;
}

.user-form-row button,
.option-button,
.product-card {
  border: 0;
  border-radius: 12px;
  font-weight: 900;
}

.user-form-row button {
  min-width: 112px;
  color: #fff;
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.user-confirmed {
  margin: 10px 0 0;
  color: var(--color-deep);
}

.status-message {
  padding: 14px 16px;
  border-radius: 12px;
  color: var(--color-muted);
  background: #f7effc;
  line-height: 1.5;
}

.error-message {
  color: #a12754;
  background: #fff0f5;
}

.notice-message {
  color: var(--color-deep);
  background: var(--color-lavender);
}

.recharge-section,
.contact-section {
  padding-top: 26px;
}

.section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 12px;
}

.section-heading h2 {
  margin-bottom: 0;
  font-size: 1.15rem;
}

.option-grid,
.product-grid,
.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.option-button {
  min-height: 46px;
  padding: 0 12px;
  color: var(--color-ink);
  background: #f3f1f5;
}

.option-button.selected {
  color: #fff;
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
}

.product-card {
  min-height: 116px;
  display: grid;
  place-content: center;
  gap: 7px;
  padding: 14px 10px;
  color: var(--color-ink);
  background: #f7f5f8;
  box-shadow: inset 0 0 0 1px rgba(131, 57, 154, 0.08);
}

.product-card:hover {
  background: #f2eaff;
}

.product-card span {
  color: var(--color-deep);
  font-size: 0.78rem;
}

.product-card b {
  font-size: 1.08rem;
}

.bonus-banner {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--color-lavender);
  color: var(--color-deep);
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.4;
}

.fine-print {
  margin: 14px 0 0;
  line-height: 1.5;
}

.payment-panel {
  display: grid;
  justify-items: center;
  text-align: center;
}

.payment-panel .section-heading,
.payment-panel .pay-lede,
.payment-panel .pay-address-row,
.payment-panel .pay-facts,
.payment-panel .fine-print {
  width: 100%;
}

.pay-lede {
  margin-bottom: 18px;
  color: var(--color-muted);
  line-height: 1.6;
}

.pay-qr {
  width: 200px;
  height: 200px;
  padding: 10px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(81, 29, 111, 0.14);
}

.pay-label {
  align-self: start;
  margin: 18px 0 8px;
  font-weight: 900;
}

.pay-address-row {
  display: flex;
  gap: 10px;
}

.pay-address-row input {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

.pay-address-row button {
  min-width: 96px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 900;
  background: linear-gradient(135deg, var(--color-purple), var(--color-pink));
}

.pay-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 18px 0 0;
}

.pay-facts div {
  padding: 12px 10px;
  border-radius: 12px;
  background: #f7f5f8;
}

.pay-facts dt {
  color: var(--color-muted);
  font-size: 0.76rem;
  font-weight: 800;
}

.pay-facts dd {
  margin: 4px 0 0;
  font-weight: 900;
}

.pay-restart {
  margin-top: 18px;
  min-height: 44px;
  padding: 0 22px;
  border: 1px solid rgba(131, 57, 154, 0.2);
  border-radius: 12px;
  color: var(--color-ink);
  font-weight: 900;
  background: #fff;
}

@media (max-width: 560px) {
  .recharge-page {
    padding: 20px 12px 48px;
  }

  .recharge-card {
    padding: 22px 16px;
    border-radius: 18px;
  }

  .recharge-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .user-form-row,
  .contact-grid {
    grid-template-columns: 1fr;
    display: grid;
  }

  .user-form-row button {
    width: 100%;
    min-height: 46px;
  }

  .option-grid,
  .product-grid,
  .pay-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pay-address-row {
    flex-direction: column;
  }

  .pay-address-row button {
    min-height: 44px;
  }
}
</style>
